import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
  getSubscriptionStatus,
  getPreferences,
  savePreferences,
} from '../services/notificationService';

const PREF_LABELS = [
  {
    key: 'class_live',
    label: "Today's Class is Live",
    desc: "Get notified the moment a class for today is posted",
  },
  {
    key: 'new_classes',
    label: 'New Classes Posted',
    desc: "New schedule available",
  },
  {
    key: 'class_reminder',
    label: 'Class Starts in 30 Min',
    desc: "Reminder to check in — only sent if you have a booking",
  },
  {
    key: 'class_cancelled',
    label: 'Class Cancellations',
    desc: "Get notified if a class you booked gets cancelled",
  },
  {
    key: 'birthday',
    label: 'Birthday Wishes',
    desc: "A personal birthday notification on your special day",
  },
];

export default function NotificationSettings() {
  const [supported, setSupported] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [prefs, setPrefs] = useState({
    class_live: true,
    class_cancelled: false,
    new_classes: true,
    class_reminder: true,
    birthday: true,
  });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!isPushSupported()) {
      setSupported(false);
      setLoading(false);
      return;
    }
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      const [status, savedPrefs] = await Promise.all([
        getSubscriptionStatus(),
        getPreferences(token),
      ]);
      setSubscribed(status);
      if (savedPrefs && !savedPrefs.detail) setPrefs(savedPrefs);
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    setToggling(true);
    try {
      await subscribeToPush(token);
      setSubscribed(true);
      toast.success('Notifications enabled');
    } catch (e) {
      toast.error(e.message || 'Could not enable notifications');
    } finally {
      setToggling(false);
    }
  };

  const handleDisable = async () => {
    setToggling(true);
    try {
      await unsubscribeFromPush(token);
      setSubscribed(false);
      toast.success('Notifications disabled');
    } catch {
      toast.error('Could not disable notifications');
    } finally {
      setToggling(false);
    }
  };

  const handleToggle = async (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    setSaving(true);
    try {
      await savePreferences(token, updated);
    } catch {
      setPrefs(prefs); // revert
      toast.error('Could not save preference');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="notification-settings-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-12">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2">
            Notifications
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">
            Choose which alerts you want to receive
          </p>
        </div>

        {!supported ? (
          <Card className="bg-[#18181B] border-[#27272A] p-8 text-center" data-testid="push-not-supported">
            <BellOff className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
            <p className="text-[#FAFAFA] font-heading font-bold uppercase text-xl mb-2">
              Not Available
            </p>
            <p className="text-[#A1A1AA]">
              Push notifications require the app to be installed on your home screen (iOS) or opened in Chrome/Edge (Android).
            </p>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Subscription Status Card */}
            <Card
              className={`border p-6 transition-all ${
                subscribed
                  ? 'bg-[#CCFF00]/5 border-[#CCFF00]/30'
                  : 'bg-[#18181B] border-[#27272A]'
              }`}
              data-testid="subscription-status-card"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-sm ${subscribed ? 'bg-[#CCFF00]/10' : 'bg-[#27272A]'}`}>
                    {subscribed ? (
                      <BellRing className="w-6 h-6 text-[#CCFF00]" />
                    ) : (
                      <Bell className="w-6 h-6 text-[#A1A1AA]" />
                    )}
                  </div>
                  <div>
                    <p className="text-[#FAFAFA] font-heading font-bold uppercase tracking-wide">
                      {subscribed ? 'Notifications On' : 'Notifications Off'}
                    </p>
                    <p className="text-sm text-[#A1A1AA]">
                      {subscribed
                        ? 'This device will receive push notifications'
                        : 'Enable to receive alerts on this device'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={subscribed ? handleDisable : handleEnable}
                  disabled={toggling}
                  data-testid={subscribed ? 'disable-notifications-btn' : 'enable-notifications-btn'}
                  className={`uppercase tracking-wider font-bold rounded-sm min-w-[120px] ${
                    subscribed
                      ? 'bg-transparent border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10'
                      : 'bg-[#CCFF00] text-black hover:bg-[#B3E600] shadow-[0_0_15px_rgba(204,255,0,0.2)]'
                  }`}
                >
                  {toggling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : subscribed ? (
                    'Turn Off'
                  ) : (
                    'Turn On'
                  )}
                </Button>
              </div>
            </Card>

            {/* Preferences — only interactive when subscribed */}
            <div>
              <h2 className="text-sm font-bold uppercase tracking-widest text-[#A1A1AA] mb-4 px-1">
                Alert Preferences
              </h2>
              <Card className="bg-[#18181B] border-[#27272A] divide-y divide-[#27272A]" data-testid="preferences-card">
                {PREF_LABELS.map(({ key, label, desc }) => (
                  <div
                    key={key}
                    className={`flex items-center justify-between gap-4 p-5 transition-opacity ${
                      !subscribed ? 'opacity-40 pointer-events-none' : ''
                    }`}
                    data-testid={`pref-row-${key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[#FAFAFA] font-semibold uppercase tracking-wide text-sm">
                        {label}
                      </p>
                      <p className="text-xs text-[#A1A1AA] mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                    <Switch
                      checked={prefs[key]}
                      onCheckedChange={(v) => handleToggle(key, v)}
                      disabled={!subscribed || saving}
                      data-testid={`pref-switch-${key}`}
                      className="data-[state=checked]:bg-[#CCFF00] shrink-0"
                    />
                  </div>
                ))}
              </Card>
            </div>

            {/* iOS tip */}
            <Card className="bg-[#18181B] border-[#27272A]/50 p-5">
              <p className="text-xs text-[#71717A] leading-relaxed">
                <span className="text-[#A1A1AA] font-semibold">iPhone users:</span> Push notifications only work when
                the app is installed — tap the share button in Safari and select "Add to Home Screen", then open the app
                from your home screen and enable notifications here.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
